<template>
  <div class="qclaw-jobs-page full-container">
    <div class="qclaw-jobs-layout">
      <div class="qclaw-jobs-panel">
        <div class="qclaw-jobs-toolbar">
          <div class="qclaw-jobs-toolbar-text">
            <span class="qclaw-jobs-toolbar-title">Agent任务</span>
            <span class="qclaw-jobs-toolbar-desc">管理 Agent 任务与查看提交成果</span>
          </div>
          <div class="qclaw-jobs-toolbar-actions">
            <Input
              v-model="groupFilter"
              placeholder="分组"
              clearable
              style="width: 120px; margin-right: 8px;"
              @on-enter="fetchList"
              @on-blur="fetchList"
              @on-clear="fetchList"
            />
            <Input
              v-model="searchText"
              placeholder="搜索名称或描述"
              clearable
              style="width: 220px; margin-right: 8px;"
            />
            <Button type="success" icon="md-add" @click="openCreate">新建任务</Button>
            <Button icon="md-refresh" @click="fetchList">刷新</Button>
          </div>
        </div>
        <div class="qclaw-jobs-table-wrap">
          <BaseTable
            ref="table"
            full
            class="qclaw-jobs-main-table"
            body-scroll-x="hidden"
            :option="{ stripe: true }"
            :columns="columns"
            :data="filteredItems"
            :loading="loading"
            border
          />
        </div>
      </div>
    </div>

    <Modal
      v-model="jobModalVisible"
      :title="jobModalTitle"
      width="560"
      :loading="jobSubmitting"
      @on-ok="handleJobSubmit"
      @on-cancel="jobModalVisible = false"
    >
      <Form ref="jobFormRef" :model="jobForm" :rules="jobRules" :label-width="88">
        <FormItem label="名称" prop="name">
          <Input v-model="jobForm.name" placeholder="任务名称" />
        </FormItem>
        <FormItem label="分组" prop="group">
          <Input v-model="jobForm.group" placeholder="分组，如：培训" />
        </FormItem>
        <FormItem label="描述" prop="description">
          <Input v-model="jobForm.description" type="textarea" :rows="3" placeholder="任务描述" />
        </FormItem>
        <FormItem label="校验规则" prop="validation">
          <Input v-model="jobForm.validation" type="textarea" :rows="4" placeholder="校验规则或提示词" />
        </FormItem>
        <FormItem label="启用">
          <i-switch v-model="jobForm.active">
            <span slot="open">启用</span>
            <span slot="close">停用</span>
          </i-switch>
        </FormItem>
      </Form>
    </Modal>

    <Modal
      v-model="validationModalVisible"
      :title="validationModalTitle"
      width="720"
      :footer-hide="true"
    >
      <Input
        v-model="validationModalText"
        type="textarea"
        :rows="16"
        readonly
        placeholder="暂无内容"
      />
    </Modal>

    <Modal
      v-model="commitDetailVisible"
      :title="commitDetailTitle"
      width="720"
      :footer-hide="true"
    >
      <Input
        v-model="commitDetailText"
        type="textarea"
        :rows="16"
        readonly
        placeholder="暂无内容"
      />
    </Modal>

    <Modal
      v-model="commentsModalVisible"
      :title="commentsModalTitle"
      width="960"
      :footer-hide="true"
      class-name="qclaw-comments-modal"
      @on-visible-change="onCommentsModalVisible"
    >
      <BaseTable
        :columns="commentColumns"
        :data="commentItems"
        border
        :loading="commentsLoading"
      />
    </Modal>
  </div>
</template>

<script>
const API_BASE = '/ai/qclaw-jobs';

function extractList(res) {
  const body = res && res.data;
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.items)) return body.items;
  if (Array.isArray(body.list)) return body.list;
  if (body.data && Array.isArray(body.data.items)) return body.data.items;
  return [];
}

function jobActive(row) {
  if (row == null) return false;
  return row.active === true || row.active === 1 || row.active === '1';
}

export default {
  data() {
    return {
      loading: false,
      items: [],
      groupFilter: '培训',
      searchText: '',
      jobModalVisible: false,
      jobModalTitle: '新建任务',
      jobSubmitting: false,
      jobEditId: null,
      jobForm: {
        name: '',
        group: '培训',
        description: '',
        validation: '',
        active: true,
      },
      jobRules: {
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        group: [{ required: true, message: '请输入分组', trigger: 'blur' }],
      },
      validationModalVisible: false,
      validationModalTitle: '校验规则',
      validationModalText: '',
      commitDetailVisible: false,
      commitDetailTitle: '提交内容',
      commitDetailText: '',
      commentsModalVisible: false,
      commentsModalTitle: '提交成果',
      commentsJobId: null,
      commentsLoading: false,
      commentItems: [],
      commentColumns: [
        { type: 'index', width: 56, title: '#' },
        {
          title: '提交内容',
          key: 'commit',
          minWidth: 280,
          align: 'left',
          render: (h, { row }) => {
            const text = row.commit || '';
            const preview = text.length > 80 ? `${text.slice(0, 80)}…` : (text || '—');
            return h('a', {
              class: 'commit-link',
              attrs: { title: text || '—' },
              on: { click: () => this.openCommitDetail(row) },
            }, preview);
          },
        },
        {
          title: '得分',
          key: 'score',
          width: 90,
          align: 'right',
          render: (h, { row }) => {
            const v = row.score;
            if (v === null || v === undefined || v === '') return h('span', '—');
            return h('span', String(v));
          },
        },
        {
          title: '提交人',
          type: 'user',
          key: 'created_by',
          width: 120,
        },
        {
          title: '提交时间',
          type: 'time',
          key: 'created_at',
          width: 156,
        },
      ],
      columns: [
        { type: 'index', width: 56, title: '#' },
        {
          title: '名称',
          key: 'name',
          minWidth: 120,
        },
        {
          title: '分组',
          key: 'group',
          width: 100,
        },
        {
          title: '描述',
          key: 'description',
          minWidth: 160,
          ellipsis: true,
          render: (h, { row }) => h('span', row.description || '—'),
        },
        {
          title: '校验规则',
          key: 'validation',
          width: 100,
          align: 'center',
          render: (h, { row }) => {
            if (!row.validation) return h('span', '—');
            return h('Button', {
              props: { size: 'small', type: 'text' },
              on: { click: () => this.openValidationView(row) },
            }, '查看');
          },
        },
        {
          title: '启用',
          key: 'active',
          width: 100,
          align: 'center',
          render: (h, { row }) => {
            if (!this.canEditOrDelete(row)) {
              return h('span', jobActive(row) ? '启用' : '停用');
            }
            return h('i-switch', {
              props: {
                value: jobActive(row),
              },
              on: {
                'on-change': (v) => this.patchActive(row, v),
              },
            }, [h('span', { slot: 'open' }, '启用'), h('span', { slot: 'close' }, '停用')]);
          },
        },
        {
          title: '提交数量',
          key: 'comments_count',
          width: 100,
          align: 'center',
          render: (h, { row }) => {
            const n = row.comments_count;
            const count = n == null || n === '' ? 0 : n;
            return h(
              'a',
              {
                class: 'comments-count-link',
                style: count ? '' : 'color:#aaa',
                on: { click: () => this.openCommentsModal(row) },
              },
              String(count),
            );
          },
        },
        {
          title: '创建人',
          type: 'user',
          key: 'created_by',
          width: 120,
        },
        {
          title: '创建时间',
          key: 'created_at',
          width: 156,
          render: (h, { row }) => h('span', this.formatTime(row.created_at)),
        },
        {
          title: '操作',
          key: 'action',
          width: 120,
          align: 'center',
          render: (h, { row }) => {
            if (!this.canEditOrDelete(row)) {
              return h('span', { style: { color: 'var(--subtext2)' } }, '—');
            }
            return h('div', [
              h('Button', {
                props: { size: 'small', type: 'text' },
                style: { marginRight: '6px' },
                on: { click: () => this.openEdit(row) },
              }, '编辑'),
              h('Button', {
                props: { size: 'small', type: 'text' },
                on: { click: () => this.confirmDelete(row) },
              }, '删除'),
            ]);
          },
        },
      ],
    };
  },
  computed: {
    session() {
      return this.$store.getters['session/session'] || {};
    },
    filteredItems() {
      let list = [...this.items];
      const q = (this.searchText || '').trim().toLowerCase();
      if (q) {
        list = list.filter(
          (item) =>
            (item.name && item.name.toLowerCase().includes(q)) ||
            (item.description && String(item.description).toLowerCase().includes(q)),
        );
      }
      return list;
    },
  },
  mounted() {
    this.$api.afterLogin(() => {
      this.fetchList();
    });
  },
  methods: {
    canEditOrDelete(row) {
      if (!this.session || !this.session.id) return false;
      return this.session.id === row.created_by || this.session.is_admin === true;
    },
    formatTime(v) {
      if (v == null || v === '') return '—';
      const d = typeof v === 'number' ? new Date(v * 1000) : new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    async fetchList() {
      this.loading = true;
      try {
        const params = {};
        if ((this.groupFilter || '').trim()) {
          params.group = this.groupFilter.trim();
        }
        const res = await this.$api.get(API_BASE, { params });
        this.items = extractList(res);
      } catch (e) {
        this.$Message.error(e.message || '加载失败');
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    resetJobForm() {
      this.jobForm = {
        name: '',
        group: this.groupFilter || '培训',
        description: '',
        validation: '',
        active: true,
      };
    },
    openCreate() {
      this.jobModalTitle = '新建任务';
      this.jobEditId = null;
      this.resetJobForm();
      this.jobModalVisible = true;
      this.$nextTick(() => {
        if (this.$refs.jobFormRef) this.$refs.jobFormRef.resetFields();
      });
    },
    openEdit(row) {
      this.jobModalTitle = '编辑任务';
      this.jobEditId = row.id;
      this.jobForm = {
        name: row.name || '',
        group: row.group || '',
        description: row.description || '',
        validation: row.validation || '',
        active: jobActive(row),
      };
      this.jobModalVisible = true;
    },
    async handleJobSubmit() {
      try {
        await this.$refs.jobFormRef.validate();
        this.jobSubmitting = true;
        const payload = {
          name: (this.jobForm.name || '').trim(),
          group: (this.jobForm.group || '').trim(),
          description: this.jobForm.description || '',
          validation: this.jobForm.validation || '',
          active: !!this.jobForm.active,
        };
        if (this.jobEditId) {
          await this.$api.patch(`${API_BASE}/${this.jobEditId}`, payload);
          this.$Message.success('已保存');
        } else {
          await this.$api.post(API_BASE, payload);
          this.$Message.success('创建成功');
        }
        this.jobModalVisible = false;
        await this.fetchList();
        return true;
      } catch (e) {
        if (e && e.message) this.$Message.error(e.message);
        return false;
      } finally {
        this.jobSubmitting = false;
      }
    },
    async patchActive(row, active) {
      try {
        await this.$api.patch(`${API_BASE}/${row.id}`, { active: !!active });
        this.$Message.success('已更新');
        await this.fetchList();
      } catch (e) {
        this.$Message.error(e.message || '更新失败');
        await this.fetchList();
      }
    },
    openValidationView(row) {
      this.validationModalTitle = `校验规则 — ${row.name || ''}`;
      this.validationModalText = row.validation || '';
      this.validationModalVisible = true;
    },
    openCommitDetail(row) {
      this.commitDetailTitle = '提交内容';
      this.commitDetailText = row.commit || '';
      this.commitDetailVisible = true;
    },
    openCommentsModal(row) {
      this.commentsJobId = row.id;
      this.commentsModalTitle = `${row.name || row.id || ''} — 提交成果`;
      this.commentItems = [];
      this.commentsModalVisible = true;
      this.fetchComments();
    },
    onCommentsModalVisible(visible) {
      if (!visible) {
        this.commentsJobId = null;
        this.commentItems = [];
      }
    },
    async fetchComments() {
      if (this.commentsJobId == null) return;
      this.commentsLoading = true;
      try {
        const res = await this.$api.get(`${API_BASE}/${this.commentsJobId}/comments`);
        this.commentItems = extractList(res);
      } catch (e) {
        this.$Message.error(e.message || '加载提交成果失败');
        this.commentItems = [];
      } finally {
        this.commentsLoading = false;
      }
    },
    confirmDelete(row) {
      this.$Modal.confirm({
        title: '删除确认',
        content: `确定删除「${row.name || row.id}」？`,
        onOk: async () => {
          try {
            await this.$api.delete(`${API_BASE}/${row.id}`);
            this.$Message.success('已删除');
            await this.fetchList();
          } catch (e) {
            this.$Message.error(e.message || '删除失败');
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.qclaw-jobs-page.full-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.03) 0%, transparent 32%);
}
.qclaw-jobs-layout {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 14px 32px 20px;
  box-sizing: border-box;
}
.qclaw-jobs-panel {
  max-width: 89%;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--bg2, #fff);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.qclaw-jobs-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
}
.qclaw-jobs-toolbar-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.qclaw-jobs-toolbar-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
}
.qclaw-jobs-toolbar-desc {
  font-size: 12px;
  color: var(--subtext2);
}
.qclaw-jobs-toolbar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;
}
.qclaw-jobs-table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0 12px 12px;
}
.comments-count-link {
  cursor: pointer;
  color: var(--primary);
}
.comments-count-link:hover {
  text-decoration: underline;
}
.commit-link {
  cursor: pointer;
  color: var(--primary);
  word-break: break-all;
}
.commit-link:hover {
  text-decoration: underline;
}
</style>
